import React, { Component, Fragment } from 'react';
import { Modifier, EditorState } from 'draft-js';
import { Map } from 'immutable';
import { Resizable } from 're-resizable';
import get from 'lodash/get';
import {
    ENTITY_TYPE, DOC_WIDTH, IFRAME_HEIGHT, DATABOX_IFRAME_HEIGHT,
} from '../utilities/CONSTANTS';
import { selectAtomicBlock, isBlockFocused } from '../utilities/blockUtilities';
import { isDataboxUrl } from '../utilities/iframeUtilities';
import { withEditorState } from './EditorStateContext';

const RESIZABLE_BLOCK_MIN_SIZE_PX = 30;

const getEntityType = entity => entity.getType().toUpperCase();

const getDefaultSize = (block, contentState) => {
    const entityKey = block.getEntityAt(0);
    const entity = contentState.getEntity(entityKey);
    const entityType = getEntityType(entity);
    const { src } = entity.getData();

    let fallbackHeight = 'auto';

    if (entityType === ENTITY_TYPE.IFRAME) {
        fallbackHeight = isDataboxUrl(src) ? DATABOX_IFRAME_HEIGHT : IFRAME_HEIGHT;
    }

    const fallbackDefault = {
        width: entityType === ENTITY_TYPE.IMAGE ? 'auto' : DOC_WIDTH,
        height: fallbackHeight,
    };

    let { width, height } = Object.assign(get(entity.getData(), 'embedly', fallbackDefault), block.get('data', Map({})).toJS());

    if (width > DOC_WIDTH) {
        const ratio = DOC_WIDTH / width;
        width = DOC_WIDTH;
        height *= ratio;
    }

    return {
        width,
        height,
    };
};

class AtomicResizable extends Component {
    onResize = (event, dir, el) => {
        const { editorState, block, setEditorState } = this.props;

        const content = editorState.getCurrentContent();

        const newContent = Modifier.setBlockData(
            content,
            editorState.getSelection().merge({
                anchorOffset: 0,
                focusOffset: 0,
                anchorKey: block.getKey(),
                focusKey: block.getKey(),
            }),
            Map({
                width: el.offsetWidth,
                height: el.offsetHeight,
            }),
        );

        setEditorState(
            EditorState.push(
                editorState,
                newContent,
            ),
        );
    };

    onSelect = () => {
        const { editorState, block, setEditorState } = this.props;
        selectAtomicBlock(block, setEditorState, editorState);
    };

    render() {
        const {
            editorState,
            children,
            block,
        } = this.props;
        const contentState = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const isFocused = isBlockFocused(false, block, selection);
        const entityKey = block.getEntityAt(0);
        const entity = contentState.getEntity(entityKey);
        const type = getEntityType(entity);
        const defaultSize = getDefaultSize(block, contentState);

        let className = 'atomic-resizable';
        if (isFocused === true) className += ' block-focused';

        return (
            <div className={className}>
                <Resizable
                    onResize={this.onResize}
                    onResizeStart={this.onSelect}
                    onClick={this.onSelect}
                    size={defaultSize}
                    minWidth={RESIZABLE_BLOCK_MIN_SIZE_PX}
                    minHeight={RESIZABLE_BLOCK_MIN_SIZE_PX}
                    lockAspectRatio={ENTITY_TYPE.IMAGE === type}
                    className="resizable-block"
                    maxWidth="100%"
                    bounds="window"
                >
                    <div className="resizable-block-inside">
                        {children}
                    </div>
                    {ENTITY_TYPE.IMAGE !== type && (
                        <Fragment>
                            <span />
                            <span />
                        </Fragment>
                    )}
                </Resizable>
            </div>
        );
    }
}

const AtomResizableWithEditorState = withEditorState(AtomicResizable);

const getResizableBlockStyle = (editorState, block) => getDefaultSize(block, editorState.getCurrentContent());

const Resized = ({ editorState, block, children }) => (
    <div className="atomic-resizable">
        <div
            className="resizable-block"
            style={getResizableBlockStyle(editorState, block)}
        >
            <div className="resizable-block-inside">
                {children}
            </div>
        </div>
    </div>
);

export default ({ readOnly, ...props }) => {
    if (readOnly === true) {
        return <Resized {...props} />;
    }

    return <AtomResizableWithEditorState {...props} />;
};
